import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  Bell,
  CalendarClock,
  CheckCircle2,
  Download,
  FileDown,
  FileSpreadsheet,
  FileText,
  Pencil,
  Play,
  Plus,
  Trash2,
  Zap,
} from "lucide-react";

import { pageTransition, formatDate } from "@/lib/utils";
import { useToast } from "@/hooks/useToast";
import {
  deleteSavedReport,
  downloadBlob,
  exportSavedReport,
  listSavedReports,
  runDueSavedReports,
  runSavedReport,
  updateSavedReport,
} from "@/api/reports";
import {
  CADENCE_LABELS,
  DATASET_LABELS,
  FORMAT_LABELS,
  type ReportExportFormat,
  type ReportScheduleCadence,
  type SavedReport,
} from "@/types/report";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Badge } from "@/components/ui/Badge";
import { Modal } from "@/components/ui/Modal";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/shared/EmptyState";

function statusBadge(status?: string | null) {
  if (!status)
    return <Badge variant="default">Never run</Badge>;
  if (status === "SUCCESS") return <Badge variant="success">Success</Badge>;
  if (status === "FAILED") return <Badge variant="danger">Failed</Badge>;
  return <Badge variant="default">{status}</Badge>;
}

const WEEKDAY_OPTIONS = [
  { value: "0", label: "Monday" },
  { value: "1", label: "Tuesday" },
  { value: "2", label: "Wednesday" },
  { value: "3", label: "Thursday" },
  { value: "4", label: "Friday" },
  { value: "5", label: "Saturday" },
  { value: "6", label: "Sunday" },
];

const DOM_OPTIONS = Array.from({ length: 28 }, (_, i) => ({
  value: String(i + 1),
  label: String(i + 1),
}));

export function SavedReportsPage() {
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [items, setItems] = useState<SavedReport[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [runningId, setRunningId] = useState<string | null>(null);
  const [exportingId, setExportingId] = useState<string | null>(null);
  const [runDueBusy, setRunDueBusy] = useState(false);

  const [editing, setEditing] = useState<SavedReport | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Schedule form state (mirrors `editing` when the modal opens)
  const [scheduled, setScheduled] = useState(false);
  const [cadence, setCadence] = useState<ReportScheduleCadence>("DAILY");
  const [dayOfWeek, setDayOfWeek] = useState<string>("0");
  const [dayOfMonth, setDayOfMonth] = useState<string>("1");
  const [recipientsRaw, setRecipientsRaw] = useState<string>("");
  const [format, setFormat] = useState<ReportExportFormat>("PDF");

  const fetchItems = useCallback(async () => {
    setIsLoading(true);
    try {
      const { items } = await listSavedReports();
      setItems(items);
    } catch {
      addToast("error", "Failed to load saved reports");
    } finally {
      setIsLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const scheduledCount = useMemo(
    () => items.filter((i) => i.is_scheduled).length,
    [items],
  );

  const openEdit = (r: SavedReport) => {
    setEditing(r);
    setScheduled(r.is_scheduled);
    setCadence(r.schedule_cadence);
    setDayOfWeek(String(r.schedule_day_of_week ?? 0));
    setDayOfMonth(String(r.schedule_day_of_month ?? 1));
    setRecipientsRaw(r.recipients.join(", "));
    setFormat(r.export_format);
  };

  const closeEdit = () => setEditing(null);

  const saveSchedule = async () => {
    if (!editing) return;
    setIsSaving(true);
    try {
      const recipients = recipientsRaw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      await updateSavedReport(editing.id, {
        is_scheduled: scheduled,
        schedule_cadence: scheduled ? cadence : "NONE",
        schedule_day_of_week:
          scheduled && cadence === "WEEKLY" ? parseInt(dayOfWeek, 10) : undefined,
        schedule_day_of_month:
          scheduled && cadence === "MONTHLY"
            ? parseInt(dayOfMonth, 10)
            : undefined,
        recipients,
        export_format: format,
      });
      addToast("success", "Saved");
      closeEdit();
      fetchItems();
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? "Failed to save";
      addToast("error", detail);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRun = async (r: SavedReport) => {
    setRunningId(r.id);
    try {
      const result = await runSavedReport(r.id);
      addToast(
        "success",
        `Ran "${r.name}" — ${result.row_count} rows`,
      );
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? "Failed to run";
      addToast("error", detail);
    } finally {
      setRunningId(null);
    }
  };

  const handleExport = async (r: SavedReport, fmt: ReportExportFormat) => {
    setExportingId(r.id);
    try {
      const { blob, filename } = await exportSavedReport(r.id, fmt);
      downloadBlob(blob, filename);
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? "Failed to export";
      addToast("error", detail);
    } finally {
      setExportingId(null);
    }
  };

  const handleDelete = async (r: SavedReport) => {
    if (!window.confirm(`Delete "${r.name}"? This can't be undone.`)) return;
    try {
      await deleteSavedReport(r.id);
      addToast("success", "Deleted");
      fetchItems();
    } catch {
      addToast("error", "Failed to delete");
    }
  };

  const handleRunDue = async () => {
    setRunDueBusy(true);
    try {
      const result = await runDueSavedReports();
      addToast(
        "success",
        `${result.delivered.length} delivered · ${result.errors.length} error(s)`,
      );
      fetchItems();
    } catch {
      addToast("error", "Failed to trigger scheduler");
    } finally {
      setRunDueBusy(false);
    }
  };

  return (
    <motion.div {...pageTransition}>
      <div className="flex items-center gap-2 mb-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/reports")}
        >
          <ArrowLeft className="h-4 w-4" />
          Back to reports
        </Button>
      </div>
      <PageHeader
        title="Saved reports"
        subtitle={
          scheduledCount > 0
            ? `${items.length} total · ${scheduledCount} on a schedule`
            : `${items.length} total`
        }
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={handleRunDue}
              isLoading={runDueBusy}
              disabled={scheduledCount === 0}
            >
              <Zap className="h-4 w-4" />
              Run due now
            </Button>
            <Button onClick={() => navigate("/reports/builder")}>
              <Plus className="h-4 w-4" />
              New report
            </Button>
          </div>
        }
      />

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Download}
          title="No saved reports yet"
          description="Build a report and save it to rerun or schedule email delivery."
          action={{
            label: "Build a report",
            onClick: () => navigate("/reports/builder"),
          }}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((r) => (
            <Card key={r.id}>
              <div className="flex items-start justify-between mb-2">
                <div className="min-w-0">
                  <h3 className="font-semibold font-heading text-text-primary truncate">
                    {r.name}
                  </h3>
                  {r.description && (
                    <p className="text-xs text-text-muted mt-0.5 line-clamp-2">
                      {r.description}
                    </p>
                  )}
                </div>
                <Badge variant="info">{DATASET_LABELS[r.dataset]}</Badge>
              </div>

              <div className="flex items-center gap-2 flex-wrap mb-3 text-xs text-text-muted">
                {r.is_scheduled ? (
                  <span className="inline-flex items-center gap-1">
                    <CalendarClock className="h-3 w-3" />
                    {CADENCE_LABELS[r.schedule_cadence]}
                    {r.next_run_at && (
                      <span>· next {formatDate(r.next_run_at)}</span>
                    )}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1">
                    <Bell className="h-3 w-3" />
                    Manual
                  </span>
                )}
                <span>· {FORMAT_LABELS[r.export_format]}</span>
                {r.recipients.length > 0 && (
                  <span>· {r.recipients.length} recipient(s)</span>
                )}
              </div>

              {r.last_run_at && (
                <div className="flex items-center gap-2 mb-3 text-xs">
                  {r.last_run_status === "FAILED" ? (
                    <AlertCircle className="h-3 w-3 text-danger" />
                  ) : (
                    <CheckCircle2 className="h-3 w-3 text-success" />
                  )}
                  <span className="text-text-muted">
                    Last run {formatDate(r.last_run_at)}
                  </span>
                  {statusBadge(r.last_run_status)}
                </div>
              )}
              {r.last_run_error && (
                <p className="text-xs text-danger bg-danger/10 rounded-md px-2 py-1 mb-3 line-clamp-2">
                  {r.last_run_error}
                </p>
              )}

              <div className="flex items-center gap-1 flex-wrap border-t border-border pt-3">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleRun(r)}
                  isLoading={runningId === r.id}
                >
                  <Play className="h-3.5 w-3.5" />
                  Run
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleExport(r, "CSV")}
                  isLoading={exportingId === r.id}
                >
                  <FileDown className="h-3.5 w-3.5" />
                  CSV
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleExport(r, "XLSX")}
                  isLoading={exportingId === r.id}
                >
                  <FileSpreadsheet className="h-3.5 w-3.5" />
                  Excel
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleExport(r, "PDF")}
                  isLoading={exportingId === r.id}
                >
                  <FileText className="h-3.5 w-3.5" />
                  PDF
                </Button>
                <div className="ml-auto flex items-center gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => openEdit(r)}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(r)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Schedule / edit modal */}
      <Modal
        isOpen={editing !== null}
        onClose={closeEdit}
        title={editing ? `Schedule: ${editing.name}` : ""}
        size="xl"
      >
        {editing && (
          <div className="space-y-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={scheduled}
                onChange={(e) => setScheduled(e.target.checked)}
                className="h-4 w-4 rounded border-border accent-accent"
              />
              <span>Send on a schedule</span>
            </label>

            {scheduled && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 rounded-lg bg-background">
                <Select
                  label="Cadence"
                  value={cadence}
                  onChange={(e) =>
                    setCadence(e.target.value as ReportScheduleCadence)
                  }
                  options={[
                    { value: "DAILY", label: "Daily" },
                    { value: "WEEKLY", label: "Weekly" },
                    { value: "MONTHLY", label: "Monthly" },
                  ]}
                />
                {cadence === "WEEKLY" && (
                  <Select
                    label="Day of week"
                    value={dayOfWeek}
                    onChange={(e) => setDayOfWeek(e.target.value)}
                    options={WEEKDAY_OPTIONS}
                  />
                )}
                {cadence === "MONTHLY" && (
                  <Select
                    label="Day of month"
                    value={dayOfMonth}
                    onChange={(e) => setDayOfMonth(e.target.value)}
                    options={DOM_OPTIONS}
                  />
                )}
              </div>
            )}

            <Input
              label="Recipients (comma separated)"
              value={recipientsRaw}
              onChange={(e) => setRecipientsRaw(e.target.value)}
              placeholder="alice@example.com, bob@example.com"
            />
            <Select
              label="Export format"
              value={format}
              onChange={(e) =>
                setFormat(e.target.value as ReportExportFormat)
              }
              options={[
                { value: "PDF", label: "PDF" },
                { value: "CSV", label: "CSV" },
                { value: "XLSX", label: "Excel" },
              ]}
            />
            <p className="text-xs text-text-muted">
              Scheduled reports fire on their next configured date and email
              the rendered file to the recipient list. You can also trigger
              all due reports immediately with the{" "}
              <span className="text-text-primary">Run due now</span> button.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={closeEdit}>
                Cancel
              </Button>
              <Button onClick={saveSchedule} isLoading={isSaving}>
                Save
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </motion.div>
  );
}
